/**
 * The effort model and the capacity assumptions, in one editable section.
 *
 * They were two read-only folds. Splitting them was wrong on both counts: every figure in the tab
 * is `demand ÷ capacity`, so the two halves of that fraction belong side by side — and the model is
 * the team's own ESTIMATE (baseline v3, calibrated 7 Sep 2026), not a measurement. Being unable to
 * re-cut it on screen made the one genuinely uncertain input the one nobody could question.
 *
 * Editing is per activity, not per rating total: a rating's hours are the sum of what recruiters
 * actually do, and typing a total would hide which activity moved. The totals column and the
 * derivation below recompute as you type.
 *
 * Every edit is validated by `reconcileState` — a negative activity would price a role below zero,
 * and an overhead/buffer pair leaving no week would divide every utilisation by nothing.
 */
import { Button } from '@finn-auto/design-system/atoms/button';
import { cn } from '../../../../../shared';
import {
  ACTIVITIES,
  BASELINE_CALIBRATED_AT,
  BASELINE_VERSION,
  RATINGS,
  SEED_CAPACITY,
  SEED_EFFORT,
  UNASSIGNED,
  cloneEffort,
  type Activity,
  type PrioritisationState,
  type Rating,
} from '../../../types/rolePrioritisation';
import {
  capacityYield,
  fteLabel,
  grossHours,
  hoursLabel,
  tierHours,
  type CompanyStats,
} from '../../../utils/rolePrioritisation';
import { Fold, Prose } from './CollapsedSection';
import { FIELD_NUM, ParkedBadge, RatingBadge } from './prioritisationUi';

const TH = 'border-b border-solid border-pewter px-2.5 py-2 text-right text-xs font-medium text-iron';
const TH_L = `${TH} text-left`;
const TD = 'border-b border-solid border-pearl px-2.5 py-1.5 text-right text-sm tabular-nums';
const TD_L = `${TD} text-left`;

export function ModelEditor({
  state,
  stats,
  onStateChange,
}: {
  state: PrioritisationState;
  stats: CompanyStats;
  onStateChange: (next: PrioritisationState, note?: string) => void;
}) {
  const { model, cap } = state;
  const gross = grossHours(state.team);
  const edited = isEdited(state);

  const setActivity = (r: Rating, a: Activity, value: string) => {
    const next = cloneEffort(model);
    next[r] = { ...next[r], [a.id]: value === '' ? 0 : Number(value) };
    // The note names the FIELD, not the value, so keystrokes coalesce into one log entry.
    onStateChange({ ...state, model: next }, `Effort model · ${r} ${a.label.toLowerCase()}`);
  };

  const setCap = (key: keyof typeof cap, value: string, label: string) =>
    onStateChange(
      { ...state, cap: { ...cap, [key]: value === '' ? 0 : Number(value) } },
      `Capacity assumption · ${label}`
    );

  return (
    <Fold
      value="model"
      title="Effort model and capacity assumptions"
      hint={`${RATINGS.map((r) => `${r} ${hoursLabel(tierHours(model, r))} h`).join(' · ')} · ×${capacityYield(cap).toFixed(2)}`}
    >
      <Prose>
        What one open role of each rating costs a recruiter per week, and how much of a contracted
        week is available to spend on roles at all. Baseline v{BASELINE_VERSION}, calibrated{' '}
        {BASELINE_CALIBRATED_AT} — an estimate by the team, so it is editable here. Every other
        figure in this tab is one of these numbers divided by the other.
      </Prose>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className={TH_L}>Priority rating</th>
              {ACTIVITIES.map((a) => (
                <th key={a.id} className={TH}>
                  {a.label}
                </th>
              ))}
              <th className={TH}>Total</th>
            </tr>
          </thead>
          <tbody>
            {RATINGS.map((r) => (
              <tr key={r}>
                <td className={TD_L}>
                  <RatingBadge rating={r} />
                </td>
                {ACTIVITIES.map((a) => (
                  <td key={a.id} className={TD}>
                    <NumField
                      label={`${r} ${a.label.toLowerCase()} hours`}
                      value={model[r][a.id]}
                      onChange={(v) => setActivity(r, a, v)}
                    />
                  </td>
                ))}
                <td className={`${TD} font-semibold`}>{hoursLabel(tierHours(model, r))} h</td>
              </tr>
            ))}
            <tr>
              <td className={TD_L}>
                <ParkedBadge />
              </td>
              {/* Parked is zero BY DEFINITION — a parked role is one nobody is working. There is
                  nothing to edit here, and an input would invite pricing a role we do not run. */}
              {ACTIVITIES.map((a) => (
                <td key={a.id} className={`${TD} text-steel`}>
                  0
                </td>
              ))}
              <td className={`${TD} font-semibold text-steel`}>0 h</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex flex-wrap items-end gap-x-5 gap-y-3 rounded-lg bg-snow px-3.5 py-3">
        <CapField
          label="Full-time week"
          unit="h"
          value={cap.fullWeekHours}
          onChange={(v) => setCap('fullWeekHours', v, 'full-time week')}
        />
        <CapField
          label="Non-req overhead"
          unit="%"
          value={cap.overheadPct}
          onChange={(v) => setCap('overheadPct', v, 'overhead')}
        />
        <CapField
          label="Absence buffer"
          unit="%"
          value={cap.bufferPct}
          onChange={(v) => setCap('bufferPct', v, 'buffer')}
        />
        <p className="max-w-[24rem] text-xs leading-4 text-iron">
          Overhead is non-req work — rituals, HM enablement, employer branding, tooling, TA&apos;s own
          hiring. The buffer is absence and volatility, taken off what overhead leaves. Both are
          global; headcount and hours come from the team table above.
        </p>
      </div>

      <div className="mt-4 text-sm">
        <Row
          k={`Gross contracted hours · ${state.team.filter((p) => p.cluster !== UNASSIGNED && p.hours > 0).length} people, ${fteLabel(gross / cap.fullWeekHours)} FTE`}
          v={`${hoursLabel(gross)} h`}
        />
        <Row
          k={`Less non-req overhead · ${cap.overheadPct}%`}
          v={`−${hoursLabel(gross * (cap.overheadPct / 100))} h`}
        />
        <Row
          k={`Less absence and volatility buffer · ${cap.bufferPct}% of net`}
          v={`−${hoursLabel(gross * (1 - cap.overheadPct / 100) * (cap.bufferPct / 100))} h`}
        />
        <Row k="Base committable capacity" v={`${hoursLabel(stats.base)} h`} strong />
        {stats.internHours > 0.005 ? (
          <Row k="Interns assigned" v={`+${hoursLabel(stats.internHours)} h`} />
        ) : null}
        {stats.volunteered > 0.005 ? (
          <Row k="Hours offered by unplanned team members" v={`+${hoursLabel(stats.volunteered)} h`} />
        ) : null}
        {stats.outHours > 0.005 ? (
          <Row k="Recruiters out of office" v={`−${hoursLabel(stats.outHours)} h`} />
        ) : null}
        <Row k="Effective capacity" v={`${hoursLabel(stats.committed)} h`} strong />
      </div>

      <Prose>
        A full-time week of {cap.fullWeekHours} h is therefore{' '}
        {hoursLabel(cap.fullWeekHours * capacityYield(cap))} committable hours — the figure one hour
        of lent support is measured against. Interviewing is the largest single activity at every
        rating, so if the model holds, interview load is the highest-leverage thing in the system:
        panel size, stage count, who runs first screens. Not sourcing capacity.
      </Prose>

      {edited ? (
        // Only when something differs — a reset that resets nothing is a button that reads as an
        // action and does nothing.
        <Button
          variant="secondary"
          size="sm"
          onClick={() =>
            onStateChange(
              { ...state, model: cloneEffort(SEED_EFFORT), cap: { ...SEED_CAPACITY } },
              `Reset the effort model and capacity assumptions to baseline v${BASELINE_VERSION}`
            )
          }
        >
          Back to baseline v{BASELINE_VERSION}
        </Button>
      ) : null}
    </Fold>
  );
}

/** Whether anything here differs from the calibrated baseline. */
function isEdited(state: PrioritisationState): boolean {
  const capChanged = (Object.keys(SEED_CAPACITY) as (keyof typeof SEED_CAPACITY)[]).some(
    (k) => state.cap[k] !== SEED_CAPACITY[k]
  );
  return (
    capChanged ||
    RATINGS.some((r) => ACTIVITIES.some((a) => state.model[r][a.id] !== SEED_EFFORT[r][a.id]))
  );
}

/**
 * A number cell in the model grid.
 *
 * `step={0.25}` because activity estimates are quarter-hours; the C tier's 1.15 h is why the field
 * is not integer-only. An empty field reads as 0 rather than NaN — clearing before retyping is the
 * normal way to edit a number, and it must not blank the whole tab while the caret sits there.
 */
function NumField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: string) => void;
}) {
  return (
    <input
      type="number"
      min={0}
      step={0.25}
      aria-label={label}
      value={String(value)}
      onChange={(e) => onChange(e.target.value)}
      className={FIELD_NUM}
    />
  );
}

function CapField({
  label,
  unit,
  value,
  onChange,
}: {
  label: string;
  unit: string;
  value: number;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-iron">
        {label} <span className="text-steel">({unit})</span>
      </span>
      <input
        type="number"
        min={0}
        step={unit === '%' ? 1 : 0.5}
        aria-label={`${label} in ${unit === '%' ? 'percent' : 'hours'}`}
        value={String(value)}
        onChange={(e) => onChange(e.target.value)}
        className={cn(FIELD_NUM, 'w-[84px]')}
      />
    </label>
  );
}

function Row({ k, v, strong }: { k: string; v: string; strong?: boolean }) {
  return (
    <div
      className={`flex justify-between gap-3 border-b border-solid border-pearl py-1.5 tabular-nums last:border-b-0 ${strong ? 'font-semibold' : ''}`}
    >
      <span className="text-asphalt">{k}</span>
      <span>{v}</span>
    </div>
  );
}
